import React, { useContext, useEffect, useRef, useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import SearchIcon from "@material-ui/icons/Search";
import InputBase from "@material-ui/core/InputBase";
import Tabs from "@material-ui/core/Tabs";
import Tab from "@material-ui/core/Tab";
import Badge from "@material-ui/core/Badge";
import MoveToInboxIcon from "@material-ui/icons/MoveToInbox";
import CheckBoxIcon from "@material-ui/icons/CheckBox";
import AddIcon from "@material-ui/icons/Add";
import Switch from "@material-ui/core/Switch";
import Select from "@material-ui/core/Select";
import Tooltip from "@material-ui/core/Tooltip";
import NewTicketModal from "../NewTicketModal";
import TicketsList from "../TicketsList";
import TabPanel from "../TabPanel";
import { i18n } from "../../translate/i18n";
import { AuthContext } from "../../context/Auth/AuthContext";
import { Can } from "../Can";
import TicketsQueueSelect from "../TicketsQueueSelect";
import { Button, Typography } from "@material-ui/core";

const useStyles = makeStyles((theme) => ({
  ticketsWrapper: {
    position: "relative",
    display: "flex",
    height: "100%",
    flexDirection: "column",
    overflow: "hidden",
    borderTopRightRadius: 0,
    borderBottomRightRadius: 0,
    backgroundColor: theme.palette.type === "dark" ? theme.palette.background.default : "#f7f9fc",
    borderRight: `1px solid ${theme.palette.type === "dark" ? "#263247" : "#e7edf5"}`,
    color: theme.palette.text.primary,
  },
  inboxHeader: {
    alignItems: "center",
    background: theme.palette.background.paper,
    borderBottom: `1px solid ${theme.palette.type === "dark" ? "#263247" : "#e7edf5"}`,
    display: "flex",
    justifyContent: "space-between",
    minHeight: 60,
    padding: theme.spacing(1, 1.5),
  },
  inboxTitle: {
    fontSize: 14,
    fontWeight: 800,
    lineHeight: 1.2,
  },
  inboxCaption: {
    color: theme.palette.type === "dark" ? "#9aa5b8" : "#718096",
    fontSize: 11,
    marginTop: 3,
  },
  newTicketButton: {
    borderRadius: 8,
    boxShadow: "none",
    fontWeight: 700,
    minWidth: "auto",
    paddingLeft: theme.spacing(1),
    paddingRight: theme.spacing(1),
  },
  tabsHeader: {
    flex: "none",
    backgroundColor: theme.palette.background.paper,
    borderBottom: `1px solid ${theme.palette.type === "dark" ? "#263247" : "#e7edf5"}`,
    padding: theme.spacing(0.75, 1.25),
    "& .MuiTabs-flexContainer": {
      gap: theme.spacing(0.75),
    },
    "& .MuiTabs-indicator": {
      display: "none",
    },
  },
  settingsIcon: {
    alignSelf: "center",
    marginLeft: "auto",
    padding: 8,
  },
  tab: {
    border: `1px solid ${theme.palette.type === "dark" ? "#334155" : "#dce4ee"}`,
    borderRadius: 7,
    fontSize: 12,
    fontWeight: 700,
    flex: 1,
    minHeight: 38,
    minWidth: 0,
    padding: theme.spacing(0.5, 1),
    textTransform: "none",
    "& .MuiTab-wrapper": {
      flexDirection: "row",
      gap: theme.spacing(0.75),
    },
    "& .MuiSvgIcon-root": {
      fontSize: 16,
    },
    "&.Mui-selected": {
      backgroundColor: theme.palette.type === "dark" ? "rgba(91, 115, 255, 0.2)" : "#eaf0ff",
      borderColor: "#5b73ff",
    },
  },
  ticketOptionsBox: {
    borderBottom: `1px solid ${theme.palette.type === "dark" ? "#263247" : "#e7edf5"}`,
    display: "flex",
    alignItems: "center",
    flexDirection: "row",
    flexWrap: "wrap",
    gap: theme.spacing(0.75),
    background: theme.palette.background.paper,
    padding: theme.spacing(0.75, 1.5),
  },
  primaryFilters: {
    alignItems: "center",
    display: "flex",
    gap: theme.spacing(0.75),
    minWidth: 0,
    flex: "0 0 auto",
    width: "auto",
  },
  groupControl: {
    alignItems: "center",
    display: "flex",
    flex: "0 0 122px",
    minWidth: 122,
    width: "auto",
  },
  serachInputWrapper: {
    flex: 1,
    background: theme.palette.type === "dark" ? "rgba(255,255,255,0.06)" : "#f2f5f9",
    display: "flex",
    border: `1px solid ${theme.palette.type === "dark" ? "#35435c" : "#dce4ee"}`,
    borderRadius: 8,
    minWidth: 180,
    padding: theme.spacing(0.25, 0.75),
  },
  searchIcon: {
    color: "grey",
    marginLeft: 6,
    marginRight: 6,
    alignSelf: "center",
  },
  searchInput: {
    flex: 1,
    border: "none",
    color: theme.palette.text.primary,
    backgroundColor: "transparent",
  },
  prioritySelect: {
    flex: "0 0 100px",
    minWidth: 100,
    width: 100,
  },
  queueFilter: {
    flex: "0 0 110px",
    minWidth: 110,
  },
  groupSelect: {
    flex: 1,
    minWidth: 0,
  },
  groupLabel: {
    color: theme.palette.type === "dark" ? "#9aa5b8" : "#718096",
    flexShrink: 0,
    fontSize: 11,
    fontWeight: 700,
    whiteSpace: "nowrap",
  },
  showAll: {
    flexShrink: 0,
    margin: 0,
  },
  ticketStatusTabs: {
    backgroundColor: theme.palette.background.paper,
    borderBottom: `1px solid ${theme.palette.type === "dark" ? "#263247" : "#e7edf5"}`,
    "& .MuiTab-root": {
      fontSize: 12,
      fontWeight: 700,
      minWidth: 0,
      textTransform: "none",
    },
  },
  filterLabel: {
    color: theme.palette.type === "dark" ? "#9aa5b8" : "#718096",
    fontSize: 11,
    fontWeight: 700,
    marginRight: theme.spacing(0.25),
    whiteSpace: "nowrap",
  },
  badge: {
    right: "-10px",
  },
  show: {
    display: "block",
  },
  hide: {
    display: "none !important",
  },
}));

const TicketsManager = () => {
  const classes = useStyles();
  const [searchParam, setSearchParam] = useState("");
  const [tab, setTab] = useState("open");
  const [tabOpen, setTabOpen] = useState("open");
  const [newTicketModalOpen, setNewTicketModalOpen] = useState(false);
  const [showAllTickets, setShowAllTickets] = useState(false);
  const [selectedPriority, setSelectedPriority] = useState("all");
  const [groupMode, setGroupMode] = useState("group");
  const searchInputRef = useRef();
  const { user } = useContext(AuthContext);
  const [openCount, setOpenCount] = useState(0);
  const [pendingCount, setPendingCount] = useState(0);
  const userQueueIds = user?.queues?.map((q) => q.id) || [];
  const [selectedQueueIds, setSelectedQueueIds] = useState(userQueueIds || []);

  useEffect(() => {
    if (user.profile.toUpperCase() === "ADMIN") {
      setShowAllTickets(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (tab === "search") {
      searchInputRef.current.focus();
      setSearchParam("");
    }
  }, [tab]);

  let searchTimeout;

  const handleSearch = (e) => {
    const searchedTerm = e.target.value.toLowerCase();

    clearTimeout(searchTimeout);

    if (searchedTerm === "") {
      setSearchParam(searchedTerm);
      setTab("open");
      return;
    }

    searchTimeout = setTimeout(() => {
      setSearchParam(searchedTerm);
    }, 500);
  };

  const handleChangeTab = (e, newValue) => {
    setTab(newValue);
  };

  const handleChangeTabOpen = (e, newValue) => {
    setTabOpen(newValue);
  };

  const applyPanelStyle = (status) => {
    if (tabOpen !== status) {
      return { width: 0, height: 0 };
    }
  };

  return (
    <Paper elevation={0} variant="outlined" className={classes.ticketsWrapper}>
      <NewTicketModal
        modalOpen={newTicketModalOpen}
        onClose={(e) => setNewTicketModalOpen(false)}
      />
      <Paper elevation={0} square className={classes.inboxHeader}>
        <div>
          <Typography className={classes.inboxTitle}>Conversaciones</Typography>
          <Typography className={classes.inboxCaption}>
            Lista de atención activa
          </Typography>
        </div>
        <Button
          className={classes.newTicketButton}
          color="primary"
          onClick={() => setNewTicketModalOpen(true)}
          size="small"
          startIcon={<AddIcon />}
          variant="contained"
        >
          {i18n.t("ticketsManager.buttons.newTicket")}
        </Button>
      </Paper>
      <Paper elevation={0} square className={classes.tabsHeader}>
        <Tabs
          value={tab}
          onChange={handleChangeTab}
          variant="scrollable"
          scrollButtons="off"
          indicatorColor="primary"
          textColor="primary"
          aria-label="Estados de conversaciones"
        >
          <Tab
            value={"open"}
            icon={<MoveToInboxIcon />}
            label={i18n.t("tickets.tabs.open.title")}
            classes={{ root: classes.tab }}
          />
          <Tab
            value={"closed"}
            icon={<CheckBoxIcon />}
            label={i18n.t("tickets.tabs.closed.title")}
            classes={{ root: classes.tab }}
          />
          <Tab
            value={"search"}
            icon={<SearchIcon />}
            label={i18n.t("tickets.tabs.search.title")}
            classes={{ root: classes.tab }}
          />
        </Tabs>
      </Paper>
      <Paper square elevation={0} className={classes.ticketOptionsBox}>
        <div className={classes.primaryFilters}>
          {tab === "search" ? (
            <div className={classes.serachInputWrapper}>
              <SearchIcon className={classes.searchIcon} />
              <InputBase
                className={classes.searchInput}
                inputRef={searchInputRef}
                placeholder={i18n.t("tickets.search.placeholder")}
                type="search"
                onChange={handleSearch}
              />
            </div>
          ) : (
            <Can
              role={user.profile}
              perform="tickets-manager:showall"
              yes={() => (
                  <Tooltip title="Ver todos">
                    <span className={classes.showAll}>
                      <Switch
                        size="small"
                      checked={showAllTickets}
                      onChange={() =>
                        setShowAllTickets((prevState) => !prevState)
                      }
                      name="showAllTickets"
                        color="primary"
                      />
                    </span>
                  </Tooltip>
              )}
            />
          )}
          <div className={classes.queueFilter}>
            <TicketsQueueSelect
              selectedQueueIds={selectedQueueIds}
              userQueues={user?.queues}
              onChange={(values) => setSelectedQueueIds(values)}
            />
          </div>
          <Select
            native
            margin="dense"
            variant="outlined"
            value={selectedPriority}
            className={classes.prioritySelect}
            onChange={(e) => setSelectedPriority(e.target.value)}
          >
            <option value="all">{i18n.t("tickets.priorityFilter.all")}</option>
            <option value="normal">{i18n.t("tickets.priorities.normal")}</option>
            <option value="high">{i18n.t("tickets.priorities.high")}</option>
            <option value="urgent">{i18n.t("tickets.priorities.urgent")}</option>
          </Select>
        </div>
        <div className={classes.groupControl}>
          <Select
            native
            margin="dense"
            variant="outlined"
            value={groupMode}
            className={classes.groupSelect}
            onChange={(e) => setGroupMode(e.target.value)}
            inputProps={{ "aria-label": "Agrupar conversaciones" }}
            >
             <option value="none">Sin agrupar</option>
            <option value="group">Por grupo</option>
          </Select>
        </div>
      </Paper>
      <TabPanel value={tab} name="open" className={classes.ticketsWrapper}>
        <Tabs
          className={classes.ticketStatusTabs}
          value={tabOpen}
          onChange={handleChangeTabOpen}
          indicatorColor="primary"
          textColor="primary"
          variant="fullWidth"
        >
          <Tab
            label={
              <Badge
                className={classes.badge}
                badgeContent={openCount}
                color="primary"
              >
                {i18n.t("ticketsList.assignedHeader")}
              </Badge>
            }
            value={"open"}
          />
          <Tab
            label={
              <Badge
                className={classes.badge}
                badgeContent={pendingCount}
                color="secondary"
              >
                {i18n.t("ticketsList.pendingHeader")}
              </Badge>
            }
            value={"pending"}
          />
        </Tabs>
        <Paper className={classes.ticketsWrapper}>
          <TicketsList
            status="open"
            showAll={showAllTickets}
            selectedQueueIds={selectedQueueIds}
            priority={selectedPriority}
            groupMode={groupMode}
            updateCount={(val) => setOpenCount(val)}
            style={applyPanelStyle("open")}
          />
          <TicketsList
            status="pending"
            selectedQueueIds={selectedQueueIds}
            priority={selectedPriority}
            groupMode={groupMode}
            updateCount={(val) => setPendingCount(val)}
            style={applyPanelStyle("pending")}
          />
        </Paper>
      </TabPanel>
      <TabPanel value={tab} name="closed" className={classes.ticketsWrapper}>
        <TicketsList
          status="closed"
          showAll={true}
          selectedQueueIds={selectedQueueIds}
          priority={selectedPriority}
          groupMode={groupMode}
        />
      </TabPanel>
      <TabPanel value={tab} name="search" className={classes.ticketsWrapper}>
        <TicketsList
          searchParam={searchParam}
          showAll={true}
          selectedQueueIds={selectedQueueIds}
          priority={selectedPriority}
          groupMode={groupMode}
        />
      </TabPanel>
    </Paper>
  );
};

export default TicketsManager;
